import React, { useRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import Badge from 'components/generic/Badge.component';
import ScoreItem from 'components/kpi/ScoreItem.component';
import TIK from 'constants/time/timeIntervalKind.const';
import TIL from 'constants/time/timeIntervalLabel.const';
import useGlobalMessages from 'hooks/useGlobalMessages.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectStylistMap } from 'selectors/salonInfo.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { formatDayToNameToMonth, formatDayToYearDetailed } from 'utils/date/format.util';
import windowScrollToPosition from 'utils/dom/windowScrollTo.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import isNotFullArray from 'utils/predicates/array/isNotFullArray.util';

import NoStylistDataPreview from './NoStylistDataPreview.component';

import CN from './ScoreCard.module.scss';

const fromKindToDisplay = (kind) => (TIK.custom === kind ? 'common.custom' : TIL[kind]);

const ScoreCard = ({ bgn, end, kind, stylistId, value, stylistsMap, isBusiness, isForecast }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const cardHeaderRef = useRef(null);

  const { strings } = useGlobalMessages();
  const performances = asArray(value);
  const hasData = isFullArray(performances);

  const stylistName = isBusiness
    ? t('staffKpisReports.kpiReports.totalBusiness')
    : fromAnyToTrimmed(stylistsMap?.[stylistId]?.userName);
  //
  const handleMouseEnter = () => {
    windowScrollToPosition(0, 0);
  };
  const handleTouchStart = () => {
    windowScrollToPosition(0, 0);
  };

  return (
    <div
      data-bem="ScoreCard"
      data-sid={String(stylistId)}
      data-kind={String(kind)}
      data-bgn={String(bgn)}
      data-end={String(end)}
      className={cn({
        [CN.component]: true,
        [CN.dark]: isDarkMode,
        [CN.hideFromPrint]: !hasData,
      })}
    >
      <div
        data-bem="ScoreCard__sticky"
        className={CN.stickyContainer}
        ref={cardHeaderRef}
        onMouseEnter={handleMouseEnter}
        onTouchStart={handleTouchStart}
      >
        <div className={CN.header}>
          <div className={CN.flex}>
            {/*
            NOTE: for stylistName when "Total business" is selected in the staff filter,
            this title should say "Business total" on all scorecards.
            It only says the Stylist name when All Staff or 1 or more stylists are selected
            */}
            <div className={CN.stylistName}>{stylistName}</div>
            <div data-bem="ScoreCard__mind" className={CN.badgeContainer}>
              <Badge variant="fine">
                <div className={CN.badgeFont}>{t(fromKindToDisplay(kind))}</div>
              </Badge>
            </div>
          </div>
        </div>

        <div data-bem="ScoreCard__toolbar" className={CN.toolbar}>
          <div data-bem="ScoreCard__time-range">
            {/* TODO: localise dates */}
            {formatDayToNameToMonth(bgn)} - {formatDayToYearDetailed(end)}
          </div>
          <div data-bem="ScoreCard__badge" className={CN.badgeContainer}>
            {isForecast ? (
              <Badge variant="draft">
                <div className={CN.badgeFont}>{t('staffKpisReports.kpiReports.scoreCard.forecast')}</div>
              </Badge>
            ) : (
              <Badge variant="sky">
                <div className={CN.badgeFont}>{t('staffKpisReports.kpiReports.scoreCard.actual')}</div>
              </Badge>
            )}
          </div>
        </div>
      </div>

      <div data-bem="ScoreCard__body" className={CN.container}>
        {isFullArray(performances) ? (
          performances.map((item) => (
            <ScoreItem key={item?.id} value={item} isForecast={isForecast} isFirst={item.id === performances[0].id} />
          ))
        ) : !isNotFullArray(strings) ? (
          <div className={CN.errorContainer}>{t('staffKpisReports.kpiReports.scoreCard.errorMessage')}</div>
        ) : !isFullArray(performances) ? (
          <div className={CN.noData}>
            <NoStylistDataPreview />
          </div>
        ) : null}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  stylistsMap: selectStylistMap(state),
});

const ScoreCardHoc = connect(mapStateToProps)(ScoreCard);

export default ScoreCardHoc;
